import { useEffect, useRef, useState } from 'react';
import { Apple, LoaderCircle, LogOut, ShieldCheck, Trash2, TriangleAlert, UserRound, X } from 'lucide-react';
import {
  deleteCurrentAccount,
  signInWithApple,
  signInWithGoogle,
  signOut,
  type SessionSnapshot,
} from '../lib/api';

const APPLE_CLIENT_ID = import.meta.env.VITE_APPLE_CLIENT_ID as string | undefined;
const APPLE_REDIRECT_URI = (import.meta.env.VITE_APPLE_REDIRECT_URI as string | undefined) ?? window.location.origin;
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;
export const ACCOUNT_DELETION_PHRASE = '我确认删除账号';

export function AuthDialog({
  session,
  onSession,
  onClose,
}: {
  session: SessionSnapshot;
  onSession: (session: SessionSnapshot) => void;
  onClose: () => void;
}) {
  const googleButton = useRef<HTMLDivElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [deletionOpen, setDeletionOpen] = useState(false);
  const [deletionText, setDeletionText] = useState('');

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || !googleButton.current) return;
    let cancelled = false;
    void loadScript('https://accounts.google.com/gsi/client', 'google-identity-services').then(() => {
      if (cancelled || !window.google || !googleButton.current) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: response => {
          setBusy(true);
          setError(undefined);
          void signInWithGoogle(response.credential)
            .then(onSession)
            .catch(cause => setError(cause instanceof Error ? cause.message : 'Google 登录失败'))
            .finally(() => setBusy(false));
        },
      });
      window.google.accounts.id.renderButton(googleButton.current, {
        theme: 'outline',
        size: 'large',
        shape: 'pill',
        text: 'continue_with',
        width: 320,
      });
    });
    return () => { cancelled = true; };
  }, [onSession]);

  const continueWithApple = async () => {
    if (!APPLE_CLIENT_ID) {
      setError('部署环境尚未配置 Apple Services ID');
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      await loadScript(
        'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/zh_CN/appleid.auth.js',
        'apple-sign-in-sdk',
      );
      if (!window.AppleID) throw new Error('Apple 登录组件加载失败');
      window.AppleID.auth.init({
        clientId: APPLE_CLIENT_ID,
        scope: 'name email',
        redirectURI: APPLE_REDIRECT_URI,
        state: crypto.randomUUID(),
        nonce: crypto.randomUUID(),
        usePopup: true,
      });
      const response = await window.AppleID.auth.signIn();
      onSession(await signInWithApple(response.authorization.id_token));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Apple 登录失败');
    } finally {
      setBusy(false);
    }
  };

  const leaveAccount = async () => {
    setBusy(true);
    setError(undefined);
    try {
      onSession(await signOut());
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : '退出登录失败');
    } finally {
      setBusy(false);
    }
  };

  const permanentlyDeleteAccount = async () => {
    if (deletionText !== ACCOUNT_DELETION_PHRASE) return;
    setBusy(true);
    setError(undefined);
    try {
      onSession(await deleteCurrentAccount());
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : '账号暂时无法删除，请稍后重试');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={event => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <section className="dialog-card auth-dialog" role="dialog" aria-modal="true" aria-labelledby="auth-title">
        <header>
          <span className="dialog-icon is-green"><UserRound aria-hidden="true" /></span>
          <div>
            <h2 id="auth-title">{session.user.isAnonymous ? '在所有设备继续' : 'Aulivis 账号'}</h2>
            <p>{session.user.isAnonymous ? '登录后，App 与网页会使用同一份笔记。' : '当前笔记会安全同步到这个账号。'}</p>
          </div>
          <button className="icon-button" onClick={onClose} disabled={busy} aria-label="关闭账号面板"><X /></button>
        </header>
        {session.user.isAnonymous ? (
          <div className="auth-options">
            <button className="apple-button" onClick={continueWithApple} disabled={busy}>
              <Apple aria-hidden="true" /> 使用 Apple 继续
            </button>
            {GOOGLE_CLIENT_ID ? <div className="google-button-host" ref={googleButton} /> : (
              <p className="provider-note">部署后配置 Google Client ID 即可启用 Google 登录。</p>
            )}
            <div className="privacy-note">
              <ShieldCheck aria-hidden="true" />
              <span>登录只用于识别你的私有云端空间，并在你明确接受后建立账号间协作。</span>
            </div>
          </div>
        ) : deletionOpen ? (
          <div className="account-deletion-panel">
            <div className="account-deletion-warning"><TriangleAlert /><span><strong>删除后无法恢复</strong><small>服务器上的账号、登录绑定、配额记录和云端使用记录将被永久删除。本机学习内容仍保留在这台设备上。</small></span></div>
            <p>删除账号不会自动取消 App Store 订阅。如有有效订阅，请先在 Apple 系统“订阅”中取消。</p>
            <label><span>输入以下内容以确认</span><input autoFocus value={deletionText} onChange={event => setDeletionText(event.target.value)} placeholder={`请输入“${ACCOUNT_DELETION_PHRASE}”`} disabled={busy} /><small>{ACCOUNT_DELETION_PHRASE}</small></label>
            <footer><button className="soft-cancel-button" onClick={() => { setDeletionOpen(false); setDeletionText(''); setError(undefined); }} disabled={busy}>取消</button><button className="account-delete-button" onClick={() => void permanentlyDeleteAccount()} disabled={busy || deletionText !== ACCOUNT_DELETION_PHRASE}>{busy ? <LoaderCircle className="spin" /> : <Trash2 />}{busy ? '正在删除…' : '永久删除账号'}</button></footer>
          </div>
        ) : (
          <div className="account-card">
            <span className="account-avatar">{initials(session.user.displayName || session.user.email || '学')}</span>
            <div>
              <strong>{session.user.displayName || 'Aulivis 学习者'}</strong>
              <small>{session.user.email || '已连接的正式账号'}</small>
            </div>
            <span className="account-state">同步中</span>
            <div className="account-actions"><button className="account-signout-button" onClick={leaveAccount} disabled={busy}><LogOut aria-hidden="true" />退出登录</button><button className="danger-ghost-button" onClick={() => { setDeletionOpen(true); setError(undefined); }} disabled={busy}><Trash2 aria-hidden="true" />删除账号</button></div>
          </div>
        )}
        {error && <p className="inline-error" role="alert">{error}</p>}
      </section>
    </div>
  );
}

function loadScript(src: string, id: string) {
  const existing = document.getElementById(id) as HTMLScriptElement | null;
  if (existing?.dataset.loaded === '1') return Promise.resolve();
  return new Promise<void>((resolve, reject) => {
    const script = existing ?? document.createElement('script');
    script.id = id;
    script.src = src;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      script.dataset.loaded = '1';
      resolve();
    };
    script.onerror = () => reject(new Error('登录组件加载失败，请检查网络后重试'));
    if (!existing) document.head.appendChild(script);
  });
}

function initials(value: string) {
  return value.trim().slice(0, 2).toUpperCase();
}
